/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * SmartGRD Component
 *
 * This component implements the SmartGRD mapping flow for camera meters and CT meters.
 * See SMARTGRD_MAPPING_STATUS.md for detailed implementation status and flow documentation.
 */

// eslint-disable-next-line import/no-extraneous-dependencies
import { BarChartOutlined, CaretDownOutlined, CaretRightOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { DndContext, DragOverlay, useDroppable } from "@dnd-kit/core";
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Input,
  Menu,
  message,
  Modal,
  Pagination,
  Spin,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import MenuDropdown from "components/MenuDropdown";
import useRoleAndPermission from "hooks/use-role-and-permission";
import useSensorflowLocation from "hooks/use-sensorflow-location";
import {
  MeterType,
  useDeleteAssetMutation,
  useDeletePositionMutation,
  useGetAssetsAndPositionsQuery,
  useNodeOnlineStatusesSubscription,
  useUnmapMeterMutation,
  useUpdateAssetParentPositionMutation,
  useUpdatePositionMutation,
} from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useState } from "react";
import { useAuth0 } from "services/auth/authService";
import { DraggableAsset, getPositionVisualType, PlainAsset } from "./AssetComponents";
import AssetModal from "./CreateAssetModal";
import CreateMeterModal from "./CreateMeterModal";
import CTMeasurementsModal from "./CTMeasurementsModal";
import { MeteringPointsList } from "./MeteringPointsList";
import MeterReadingsModal from "./MeterReadingsModal";
import PositionMeasurementsModal from "./PositionMeasurementsModal";
import SmartGRDHolisticsBoard from "./SmartGRDHolisticsBoard";
import CreateZoneModal from "./ZoneModal/CreateZoneModal";

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

// Get dashboard embedId from environment variable
const smartGRDDashboardEmbedId =
  process.env.REACT_APP_HOLISTICS_SMART_GRD_DASHBOARD_EMBED_ID || "2495b5c8a0be4a14adde3403";

// Get position measurements embed ID
const positionMeasurementsEmbedId = process.env.REACT_APP_HOLISTICS_POSITION_MEASUREMENTS_EMBED_ID || "";

function DroppableZone({ position, children }: any) {
  const { setNodeRef, isOver } = useDroppable({ id: position.positionId });
  const visual = getPositionVisualType(position);
  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: 100,
        minWidth: 180,
        margin: 2,
        padding: 4,
        background: isOver ? "#e3f2fd" : "#f9f9f9",
        border: isOver ? `2px solid #1976d2` : `1px dashed ${visual.color}`,
        borderRadius: 4,
        flex: 1,
      }}
    >
      {children}
    </div>
  );
}

const SmartGRD = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isZoneModalVisible, setIsZoneModalVisible] = useState(false);
  const [isMeteringModalVisible, setIsMeteringModalVisible] = useState(false);
  const [isCTMeasurementsModalVisible, setIsCTMeasurementsModalVisible] = useState(false);
  const [isMeterReadingsModalVisible, setIsMeterReadingsModalVisible] = useState(false);
  const [isPositionMeasurementsModalVisible, setIsPositionMeasurementsModalVisible] = useState(false);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [meteringPositionId, setMeteringPositionId] = useState<string | null>(null);
  const [selectedCTMeteringPointId, setSelectedCTMeteringPointId] = useState<string | null>(null);
  const [selectedCTAssetName, setSelectedCTAssetName] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedAssetName, setSelectedAssetName] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [selectedPositionName, setSelectedPositionName] = useState<string | null>(null);
  const [selectedPositionIdForAsset, setSelectedPositionIdForAsset] = useState<string | null>(null);
  const [editingAsset, setEditingAsset] = useState<any | null>(null);
  const [collapsedPositions, setCollapsedPositions] = useState<Set<string>>(new Set());
  const [renamingPosition, setRenamingPosition] = useState<any | null>(null);
  const [newPositionName, setNewPositionName] = useState<string>("");
  const roleAndPermission = useRoleAndPermission();
  const { locationId /* locationName */ } = useSensorflowLocation();
  const { user } = useAuth0();
  const { data, loading, error, refetch } = useGetAssetsAndPositionsQuery({
    variables: { locationId },
    skip: !locationId,
  });

  // Subscribe to node online statuses for real-time meter status updates
  const { data: nodeOnlineStatusesData } = useNodeOnlineStatusesSubscription({
    variables: { locationId },
    skip: !locationId,
  });
  const [assetPositions, setAssetPositions] = useState<{ [assetId: string]: string }>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [searchTerm, setSearchTerm] = useState("");
  const [assetSearchTerm, setAssetSearchTerm] = useState("");
  const [updateAssetParentPosition, { loading: updating }] = useUpdateAssetParentPositionMutation();
  const [unmapMeter, { loading: unmappingMeter }] = useUnmapMeterMutation();
  const [updatePosition, { loading: updatingPosition }] = useUpdatePositionMutation({
    onCompleted: () => {
      message.success("Position name updated successfully!");
      refetch();
      closeRenameModal();
    },
    onError: (updateError) => {
      Modal.error({
        title: "Error",
        content: updateError.message || "Failed to update position name",
      });
    },
  });
  const [positionTypeFilter, setPositionTypeFilter] = useState<string[]>(["key", "group"]);
  // Delete mutations
  const [deleteAsset, { loading: deletingAsset }] = useDeleteAssetMutation({
    onCompleted: () => {
      message.success("Asset deleted successfully!");
      refetch();
    },
    onError: (deleteError) => {
      Modal.error({
        title: "Error",
        content: deleteError.message || "Failed to delete asset",
      });
    },
  });

  const [deletePosition, { loading: deletingPosition }] = useDeletePositionMutation({
    onCompleted: () => {
      message.success("Zone/position deleted successfully!");
      refetch();
    },
    onError: (deleteError) => {
      Modal.error({
        title: "Error",
        content: deleteError.message || "Failed to delete zone/position",
      });
    },
  });

  // Helper function to toggle position collapse state
  const togglePositionCollapse = (positionId: string) => {
    setCollapsedPositions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(positionId)) {
        newSet.delete(positionId);
      } else {
        newSet.add(positionId);
      }
      return newSet;
    });
  };

  // Safety check functions for deletion
  const canDeleteAsset = (assetId: string): { canDelete: boolean; reason?: string } => {
    // Check if asset is being metered (has active stream mappings)
    const isMetered = data?.sensorflow_stream_mappings?.some(
      (mapping: any) => mapping.subjectId === assetId && !mapping.decommissionedTime
    );

    if (isMetered) {
      return { canDelete: false, reason: "Asset is currently being metered. Unmap the meter first." };
    }

    return { canDelete: true };
  };

  const canDeletePosition = (positionId: string): { canDelete: boolean; reason?: string } => {
    // Check if position is being metered (has active stream mappings)
    const isMetered = data?.sensorflow_stream_mappings?.some(
      (mapping: any) => mapping.subjectId === positionId && !mapping.decommissionedTime
    );

    if (isMetered) {
      return { canDelete: false, reason: "Zone/position is currently being metered. Unmap the meter first." };
    }

    // Check if other positions point to this as parent (has child positions)
    const hasChildPositions = data?.positions?.some(
      (pos: any) => pos.parentPositionId === positionId && !pos.deletedAt
    );

    if (hasChildPositions) {
      return { canDelete: false, reason: "Zone/position has child positions. Move or delete child positions first." };
    }

    // Check if any assets point to this as parent
    const hasChildAssets = data?.sensorflow_assets?.some(
      (asset: any) => asset.parentPositionId === positionId && !asset.deletedAt
    );

    if (hasChildAssets) {
      return { canDelete: false, reason: "Zone/position contains assets. Move or delete assets first." };
    }

    return { canDelete: true };
  };

  // Helper functions to check for active meters using stream_mappings
  const hasActiveMeter = (positionId: string) => {
    if (!data?.sensorflow_stream_mappings) return false;

    return data.sensorflow_stream_mappings.some(
      (mapping: any) => mapping.subjectId === positionId && !mapping.decommissionedTime
    );
  };

  const hasActiveAssetMeter = (assetId: string) => {
    if (!data?.sensorflow_stream_mappings) return false;

    return data.sensorflow_stream_mappings.some(
      (mapping: any) => mapping.subjectId === assetId && !mapping.decommissionedTime
    );
  };

  // Helper function to check if a position is an autoset position
  const isAutosetPosition = (position: any) => {
    return position.positionFunction === "autoset" || position.positionFunction === "AUTOSET";
  };

  // Helper function to check if an asset is a CT node with active meter
  const isCTNode = (assetId: string) => {
    if (!data?.sensorflow_stream_mappings || !data?.positions) return false;

    // Find stream mapping for this asset
    const streamMapping = data.sensorflow_stream_mappings.find(
      (mapping: any) => mapping.subjectId === assetId && !mapping.decommissionedTime
    );

    if (!streamMapping) return false;

    // Find the metering point position
    const meteringPosition = data.positions.find(
      (pos: any) => pos.positionId === streamMapping.meteringPointPositionId
    );

    if (!meteringPosition) return false;

    // Check if this position has CT node slots
    if (meteringPosition.nodeSlots && meteringPosition.nodeSlots.length > 0) {
      const hasCtNode = meteringPosition.nodeSlots.some((slot: any) => {
        // Camera meters have subType: 'camera' - these should NOT be considered CT nodes
        if (slot.subType === "camera") {
          return false;
        }

        // Additional check: Milesight devices are Camera meters, not CT meters
        // This handles legacy data where subType might not be set properly
        if (meteringPosition.positionName && meteringPosition.positionName.toLowerCase().includes("milesight")) {
          return false;
        }

        // Only CT meters (nodeType: 'energy') should be considered CT nodes
        return slot.nodeType === "energy" && !slot.slotRemoveDate;
      });
      return hasCtNode;
    }

    return false;
  };

  // Helper function to get the metering point position ID for a CT asset
  const getCTMeteringPointId = (assetId: string) => {
    if (!data?.sensorflow_stream_mappings || !data?.positions) return null;

    // Find stream mapping for this asset
    const streamMapping = data.sensorflow_stream_mappings.find(
      (mapping: any) => mapping.subjectId === assetId && !mapping.decommissionedTime
    );

    if (!streamMapping) return null;

    // Find the metering point position
    const meteringPosition = data.positions.find(
      (pos: any) => pos.positionId === streamMapping.meteringPointPositionId
    );

    if (!meteringPosition) return null;

    // Check if this position has CT node slots
    if (meteringPosition.nodeSlots && meteringPosition.nodeSlots.length > 0) {
      const hasCtNode = meteringPosition.nodeSlots.some((slot: any) => {
        // CT nodes typically have nodeType like 'energy', 'gas', 'water', 'power'
        return slot.nodeType && ["energy", "gas", "water", "power"].includes(slot.nodeType) && !slot.slotRemoveDate;
      });

      if (hasCtNode) {
        return streamMapping.meteringPointPositionId;
      }
    }

    return null;
  };

  // Helper function to get meter online status for a position
  const getMeterOnlineStatus = (positionId: string) => {
    if (!nodeOnlineStatusesData?.nodeOnlineStatuses || !data?.sensorflow_stream_mappings || !data?.positions)
      return null;

    // Find stream mapping for this position
    const streamMapping = data.sensorflow_stream_mappings.find(
      (mapping: any) => mapping.subjectId === positionId && !mapping.decommissionedTime
    );

    if (!streamMapping) return null;

    // Find the metering point position
    const meteringPosition = data.positions.find(
      (pos: any) => pos.positionId === streamMapping.meteringPointPositionId
    );

    if (!meteringPosition || !meteringPosition.nodeSlots) return null;

    // Get the node MAC ID from the metering point position
    let nodeMacId: string | null = null;
    meteringPosition.nodeSlots.find((slot: any) => {
      if (slot.slotMappings && slot.slotMappings.length > 0) {
        const activeMapping = slot.slotMappings.find((mapping: any) => !mapping.decomissionedTime);
        if (activeMapping && activeMapping.nodeMacId) {
          nodeMacId = activeMapping.nodeMacId;
          return true;
        }
      }
      return false;
    });

    if (!nodeMacId) return null;

    // Find the node online status
    const nodeStatus = nodeOnlineStatusesData.nodeOnlineStatuses.find((status: any) => status.nodeMacId === nodeMacId);

    return nodeStatus
      ? {
          status: nodeStatus.nodeStatus,
          statusInInstallationMode: nodeStatus.nodeStatusInInstallationMode,
          lastMeasurement: nodeStatus.lastMeasurement,
          lastTransmission: nodeStatus.lastTransmission,
        }
      : null;
  };

  // Helper function to get meter online status for an asset
  const getAssetMeterOnlineStatus = (assetId: string) => {
    if (!nodeOnlineStatusesData?.nodeOnlineStatuses || !data?.sensorflow_stream_mappings || !data?.positions)
      return null;

    // Find stream mapping for this asset
    const streamMapping = data.sensorflow_stream_mappings.find(
      (mapping: any) => mapping.subjectId === assetId && !mapping.decommissionedTime
    );

    if (!streamMapping) return null;

    // Find the metering point position
    const meteringPosition = data.positions.find(
      (pos: any) => pos.positionId === streamMapping.meteringPointPositionId
    );

    if (!meteringPosition || !meteringPosition.nodeSlots) return null;

    // Get the node MAC ID from the metering point position
    let nodeMacId: string | null = null;
    meteringPosition.nodeSlots.find((slot: any) => {
      if (slot.slotMappings && slot.slotMappings.length > 0) {
        const activeMapping = slot.slotMappings.find((mapping: any) => !mapping.decomissionedTime);
        if (activeMapping && activeMapping.nodeMacId) {
          nodeMacId = activeMapping.nodeMacId;
          return true;
        }
      }
      return false;
    });

    if (!nodeMacId) return null;

    // Find the node online status
    const nodeStatus = nodeOnlineStatusesData.nodeOnlineStatuses.find((status: any) => status.nodeMacId === nodeMacId);

    return nodeStatus
      ? {
          status: nodeStatus.nodeStatus,
          statusInInstallationMode: nodeStatus.nodeStatusInInstallationMode,
          lastMeasurement: nodeStatus.lastMeasurement,
          lastTransmission: nodeStatus.lastTransmission,
        }
      : null;
  };

  // Helper function to get meter icon with online/offline status
  const getMeterIcon = (positionId: string, isAsset = false) => {
    const hasActiveMeterForPosition = isAsset ? hasActiveAssetMeter(positionId) : hasActiveMeter(positionId);

    if (!hasActiveMeterForPosition) {
      return null;
    }

    const meterStatus = isAsset ? getAssetMeterOnlineStatus(positionId) : getMeterOnlineStatus(positionId);

    // Determine color and title based on status
    let color = "#52c41a"; // Default green for online
    let title = "Meter online";

    if (meterStatus) {
      const status = meterStatus.status?.toUpperCase();
      switch (status) {
        case "ONLINE":
          color = "#52c41a";
          title = "Meter online";
          break;
        case "OFFLINE":
          color = "#ff4d4f";
          title = "Meter offline";
          break;
        case "BOOTED":
          color = "#1890ff";
          title = "Meter booted";
          break;
        case "RE-JOINING":
        case "JOINING":
          color = "#faad14";
          title = "Meter connecting";
          break;
        default:
          color = "#d9d9d9";
          title = "Meter status unknown";
          break;
      }
    } else {
      // No status info available, show as unknown
      color = "#d9d9d9";
      title = "Meter status unknown";
    }

    return (
      <ThunderboltOutlined
        style={{
          marginLeft: 4,
          color,
          fontSize: 11,
        }}
        title={title}
      />
    );
  };

  // Handler for opening CT measurements modal
  const handleViewCTMeasurements = (assetId: string, assetName: string) => {
    const meteringPointId = getCTMeteringPointId(assetId);
    if (meteringPointId) {
      setSelectedCTMeteringPointId(meteringPointId);
      setSelectedCTAssetName(assetName);
      setIsCTMeasurementsModalVisible(true);
    }
  };

  // Handler for closing CT measurements modal
  const handleCloseCTMeasurements = () => {
    setIsCTMeasurementsModalVisible(false);
    setSelectedCTMeteringPointId(null);
    setSelectedCTAssetName(null);
  };

  // Handler for opening meter readings modal
  const handleViewMeterReadings = (assetId: string, assetName: string) => {
    setSelectedAssetId(assetId);
    setSelectedAssetName(assetName);
    setIsMeterReadingsModalVisible(true);
  };

  // Handler for closing meter readings modal
  const handleCloseMeterReadings = () => {
    setIsMeterReadingsModalVisible(false);
    setSelectedAssetId(null);
    setSelectedAssetName(null);
  };

  const openModal = (positionId?: string) => {
    setIsModalVisible(true);
    setSelectedPositionIdForAsset(positionId || null);
    setEditingAsset(null); // Ensure we're in create mode
  };
  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedPositionIdForAsset(null);
    setEditingAsset(null);
  };

  const openEditModal = (asset: any) => {
    setIsModalVisible(true);
    setEditingAsset(asset);
    setSelectedPositionIdForAsset(null); // Not needed for edit mode
  };

  const openZoneModal = () => setIsZoneModalVisible(true);
  const closeZoneModal = () => setIsZoneModalVisible(false);

  const openRenameModal = (position: any) => {
    setIsRenameModalVisible(true);
    setRenamingPosition(position);
    setNewPositionName(position.positionName);
  };

  const closeRenameModal = () => {
    setIsRenameModalVisible(false);
    setRenamingPosition(null);
    setNewPositionName("");
  };

  const handleRenamePosition = () => {
    if (!renamingPosition || !newPositionName.trim()) return;

    const trimmedName = newPositionName.trim();
    if (trimmedName === renamingPosition.positionName) {
      closeRenameModal();
      return;
    }

    updatePosition({
      variables: {
        positionId: renamingPosition.positionId,
        updatePositionInput: {
          positionName: trimmedName,
        },
      },
    });
  };

  React.useEffect(() => {
    if (data?.sensorflow_assets) {
      const initial = Object.fromEntries(data.sensorflow_assets.map((a: any) => [a.id, a.parentPositionId]));
      setAssetPositions(initial);
    }
  }, [data]);

  // Collapse positions with content by default when data loads
  React.useEffect(() => {
    if (data?.positions && data?.sensorflow_assets) {
      const positions = (data.positions || []).filter((pos: any) => !pos.deletedAt);
      const assets = (data.sensorflow_assets || []).filter((asset: any) => !asset.deletedAt);

      // Build assetsByPosition map
      const assetsByPosition: { [positionId: string]: any[] } = {};
      positions.forEach((pos: any) => {
        assetsByPosition[pos.positionId] = [];
      });
      assets.forEach((asset: any) => {
        const posId = asset.parentPositionId;
        if (!assetsByPosition[posId]) assetsByPosition[posId] = [];
        assetsByPosition[posId].push(asset);
      });

      // Build position map with children
      const positionMap: { [id: string]: any } = {};
      positions.forEach((pos: any) => {
        positionMap[pos.positionId] = { ...pos, children: [] };
      });
      positions.forEach((pos: any) => {
        if (pos.parentPositionId && positionMap[pos.parentPositionId]) {
          positionMap[pos.parentPositionId].children.push(positionMap[pos.positionId]);
        }
      });

      // Find all positions that have content (assets or children) and collapse them
      const positionsToCollapse = new Set<string>();
      positions.forEach((position: any) => {
        const hasContent =
          assetsByPosition[position.positionId]?.length > 0 ||
          (positionMap[position.positionId]?.children &&
            positionMap[position.positionId].children.filter((child: any) => child.positionType !== "metering_point")
              .length > 0);

        if (hasContent) {
          positionsToCollapse.add(position.positionId);
        }
      });

      setCollapsedPositions(positionsToCollapse);
    }
  }, [data]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading assets/positions</div>;
  if (!data) return null;

  // Filter out deleted positions and assets
  const positions = (data.positions || []).filter((pos: any) => !pos.deletedAt);
  const assets = (data.sensorflow_assets || []).filter((asset: any) => !asset.deletedAt);

  // Build a map of positions by id
  const positionMap: { [id: string]: any } = {};
  positions.forEach((pos: any) => {
    positionMap[pos.positionId] = { ...pos, children: [] };
  });

  // Build the tree: nest all positions under their parent if parent exists
  positions.forEach((pos: any) => {
    if (pos.parentPositionId && positionMap[pos.parentPositionId]) {
      positionMap[pos.parentPositionId].children.push(positionMap[pos.positionId]);
    }
  });

  // Only paginate and render as top-level: keys (positionType: 'key', positionFunction: 'identifier'), legacy zones (positionType: 'zone'), and all positions with group function (positionFunction: 'group'). Exclude root positions.
  const topLevelPositions = positions
    .filter(
      (pos: any) =>
        pos.positionType !== "root" &&
        ((pos.positionType === "key" && pos.positionFunction === "identifier") ||
          pos.positionType === "zone" ||
          pos.positionFunction === "group")
    )
    .map((pos: any) => positionMap[pos.positionId]);

  // Group assets by their current parentPositionId
  const assetsByPosition: { [positionId: string]: any[] } = {};
  positions.forEach((pos: any) => {
    assetsByPosition[pos.positionId] = [];
  });
  assets.forEach((asset: any) => {
    const posId = assetPositions[asset.id] || asset.parentPositionId;
    if (!assetsByPosition[posId]) assetsByPosition[posId] = [];
    assetsByPosition[posId].push(asset);
  });

  // Helper: case-insensitive match
  const matchesSearch = (str: string) => str && str.toLowerCase().includes(searchTerm.toLowerCase());

  // Helper: get visual type for filtering
  const getPositionTypeForFilter = (position: any) => {
    if (position.positionType === "key" && position.positionFunction === "identifier") return "key";
    if (position.positionType === "zone" || position.positionFunction === "group") return "group";
    return "other";
  };

  // Filtered top-level positions (keys and zones) and their children/assets
  const filteredTopLevelPositions = topLevelPositions.filter((pos) => {
    // Filter by type
    const type = getPositionTypeForFilter(pos);
    if (!positionTypeFilter.includes(type)) return false;
    // Check position name
    if (matchesSearch(pos.positionName)) return true;
    // Check children positions (autosets, groups, etc.)
    if (pos.children && pos.children.some((child: any) => matchesSearch(child.positionName))) return true;
    // Check assets under this position or its children
    const posAssets = assetsByPosition[pos.positionId] || [];
    if (posAssets.some((asset: any) => matchesSearch(asset.type) || matchesSearch(asset.description))) return true;
    if (
      pos.children &&
      pos.children.some((child: any) => {
        const childAssets = assetsByPosition[child.positionId] || [];
        return childAssets.some((asset: any) => matchesSearch(asset.type) || matchesSearch(asset.description));
      })
    )
      return true;
    return false;
  });

  // Pagination logic (only for filtered top-level positions)
  const totalPositions = filteredTopLevelPositions.length;
  const paginatedPositions = filteredTopLevelPositions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active && over && assetPositions[active.id] !== over.id) {
      Modal.confirm({
        title: "Confirm Move",
        content: "Are you sure you want to move this asset to a new position?",
        okText: "Yes",
        cancelText: "No",
        onOk: async () => {
          try {
            await updateAssetParentPosition({
              variables: {
                id: active.id,
                parentPositionId: over.id,
              },
            });
            setAssetPositions((prev) => ({ ...prev, [active.id]: over.id }));
          } catch (err: any) {
            Modal.error({
              title: "Error",
              content: err.message || "Failed to update asset position.",
            });
          }
        },
        okButtonProps: { loading: updating },
      });
    }
    setActiveId(null);
  };

  const openMeteringModal = (positionId?: string) => {
    setIsMeteringModalVisible(true);
    if (positionId) {
      setMeteringPositionId(positionId);
    } else {
      setMeteringPositionId(null);
    }
  };
  const closeMeteringModal = () => {
    setIsMeteringModalVisible(false);
    setMeteringPositionId(null);
  };

  // Helper: get position name by id
  const getPositionName = (positionId: string) => {
    const pos = positions.find((p: any) => p.positionId === positionId);
    return pos ? pos.positionName : "Unmapped";
  };

  // Helper: get the currently dragged asset
  const activeAsset = activeId ? assets.find((a: any) => a.id === activeId) : null;

  // --- NEW: Right pane asset list ---
  // Helper: case-insensitive match for asset pane
  const matchesAssetSearch = (asset: any) => {
    if (!assetSearchTerm) return true;
    return (
      (asset.type && asset.type.toLowerCase().includes(assetSearchTerm.toLowerCase())) ||
      (asset.description && asset.description.toLowerCase().includes(assetSearchTerm.toLowerCase()))
    );
  };

  const renderAssetList = () => (
    <div
      style={{
        background: "#fff",
        border: "none",
        borderRadius: 0,
        padding: 0,
        minWidth: 0,
        maxWidth: "none",
        boxShadow: "none",
      }}
    >
      <Input.Search
        placeholder="Search assets"
        value={assetSearchTerm}
        onChange={(e) => setAssetSearchTerm(e.target.value)}
        allowClear
        style={{ marginBottom: 8 }}
      />
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 12 }}>All Assets</div>
      {assets.filter(matchesAssetSearch).map((asset: any) => (
        <div key={asset.id} style={{ marginBottom: 4 }}>
          {activeId === asset.id ? (
            // Placeholder while dragging this asset
            <div style={{ height: 56 }} />
          ) : (
            <DraggableAsset
              asset={asset}
              positionName={getPositionName(assetPositions[asset.id] || asset.parentPositionId)}
              hasMeter={hasActiveAssetMeter(asset.id)}
              isCTNode={isCTNode(asset.id)}
              meterIcon={getMeterIcon(asset.id, true)}
              onEdit={() => roleAndPermission.isPC() && openEditModal(asset)}
              onViewMeasurements={() => handleViewCTMeasurements(asset.id, asset.description || asset.type)}
              onViewMeterReadings={() => handleViewMeterReadings(asset.id, asset.description || asset.type)}
              onLocate={() => {
                setSearchTerm(asset.description || asset.name || asset.type || "");
                setCurrentPage(1);
              }}
              onEditAsset={openEditModal}
              onCreateMeter={openMeteringModal}
              onUnmapMeter={handleUnmapAssetMeter}
              hasActiveAssetMeter={hasActiveAssetMeter(asset.id)}
              isPC={roleAndPermission.isPC()}
              phases={asset.numOfPhases}
              onDeleteAsset={handleDeleteAsset}
            />
          )}
        </div>
      ))}
    </div>
  );

  // Recursive render for position tree
  const renderPositionTree = (position: any, level = 0) => {
    // Don't render metering point positions in droppable zones
    if (position.positionType === "metering_point") {
      return null;
    }

    const isCollapsed = collapsedPositions.has(position.positionId);
    const hasContent =
      assetsByPosition[position.positionId]?.length > 0 ||
      (position.children &&
        position.children.filter((child: any) => child.positionType !== "metering_point").length > 0);

    const positionMenu = (
      <Menu
        style={{ minWidth: 180 }}
        items={[
          {
            key: "create-asset",
            label: "Create Asset",
            onClick: () => openModal(position.positionId),
          },
          {
            type: "divider",
          },
          {
            key: "view-position-measurements",
            label: "View Measurements",
            disabled: !positionMeasurementsEmbedId,
            onClick: () => handleViewPositionMeasurements(position.positionId, position.positionName),
          },
          {
            type: "divider",
          },
          {
            key: "add-metering-point",
            label: (() => {
              if (hasActiveMeter(position.positionId)) {
                return "Create Meter (Already has meter)";
              }
              if (isAutosetPosition(position)) {
                return "Create Meter (Not allowed on autoset)";
              }
              return "Create Meter";
            })(),
            disabled: hasActiveMeter(position.positionId) || isAutosetPosition(position),
            onClick: () => openMeteringModal(position.positionId),
          },
          {
            key: "unmap-meter",
            label: "Unmap Meter",
            danger: true,
            disabled: !hasActiveMeter(position.positionId),
            onClick: () => handleUnmapPositionMeter(position),
          },
          {
            key: "rename-position",
            label: "Rename Position",
            onClick: () => openRenameModal(position),
          },
          {
            type: "divider",
          },
          {
            key: "delete-position",
            label: "Delete Zone/Position",
            danger: true,
            disabled: !canDeletePosition(position.positionId).canDelete,
            onClick: () => handleDeletePosition(position),
          },
        ]}
      />
    );

    // Don't show menu for metering point positions (redundant now, but keeping for safety)
    const isMeteringPosition = position.positionType === "metering_point";

    return (
      <DroppableZone key={position.positionId} position={position}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Position name row with menu and collapse toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: 600,
              marginBottom: 1,
              color: getPositionVisualType(position).color,
            }}
          >
            {hasContent && (
              <span
                role="button"
                tabIndex={0}
                style={{
                  cursor: "pointer",
                  marginRight: 4,
                  display: "flex",
                  alignItems: "center",
                  color: "#666",
                }}
                onClick={() => togglePositionCollapse(position.positionId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    togglePositionCollapse(position.positionId);
                  }
                }}
                aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${position.positionName} content`}
              >
                {isCollapsed ? (
                  <CaretRightOutlined style={{ fontSize: 10 }} />
                ) : (
                  <CaretDownOutlined style={{ fontSize: 10 }} />
                )}
              </span>
            )}
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: getPositionVisualType(position).color,
                borderRadius: 2,
                marginRight: 6,
                border: "1px solid #ccc",
              }}
            />
            <span style={{ fontSize: 12 }}>{position.positionName}</span>
            <span style={{ marginLeft: 4, fontSize: 9, color: "#888" }}>({getPositionVisualType(position).label})</span>
            {getMeterIcon(position.positionId, false)}
            {roleAndPermission.isPC() && !isMeteringPosition && (
              <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
                {/* Standalone View Measurements Button for Positions */}
                {positionMeasurementsEmbedId && (
                  <Tooltip title="View Measurements">
                    <Button
                      type="text"
                      size="small"
                      icon={<BarChartOutlined />}
                      onClick={() => handleViewPositionMeasurements(position.positionId, position.positionName)}
                      style={{
                        padding: "4px 6px",
                        minWidth: "auto",
                        height: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  </Tooltip>
                )}
                <MenuDropdown menu={positionMenu} />
              </span>
            )}
          </div>

          {/* Collapsible content */}
          {!isCollapsed && (
            <>
              {assetsByPosition[position.positionId]?.map((asset) => {
                return (
                  <div key={asset.id} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 1 }}>
                    <PlainAsset
                      asset={asset}
                      positionName={position.positionName}
                      hasMeter={hasActiveAssetMeter(asset.id)}
                      isCTNode={isCTNode(asset.id)}
                      meterIcon={getMeterIcon(asset.id, true)}
                      onViewMeasurements={() => handleViewCTMeasurements(asset.id, asset.description || asset.type)}
                      onViewMeterReadings={() => handleViewMeterReadings(asset.id, asset.description || asset.type)}
                      onEditAsset={openEditModal}
                      onCreateMeter={openMeteringModal}
                      onUnmapMeter={handleUnmapAssetMeter}
                      hasActiveAssetMeter={hasActiveAssetMeter(asset.id)}
                      isPC={roleAndPermission.isPC()}
                      phases={asset.numOfPhases}
                      onDeleteAsset={handleDeleteAsset}
                    />
                  </div>
                );
              })}
              {position.children && position.children.length > 0 && (
                <div style={{ marginTop: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  {position.children
                    .filter((child: any) => child.positionType !== "metering_point")
                    .map((child: any) => renderPositionTree(child, level + 1))}
                </div>
              )}
            </>
          )}
        </div>
      </DroppableZone>
    );
  };

  const renderDashboard = () => {
    const filters = {
      f1: {
        default_condition: {
          operator: "is",
          values: [locationId],
          modifier: null,
        },
      },
    };

    return (
      <div className="dashboard-content">
        <Card>
          <Typography>
            <Title level={4}>SmartGRD Dashboard</Title>
          </Typography>
          <SmartGRDHolisticsBoard locationId={locationId || ""} embedId={smartGRDDashboardEmbedId} filters={filters} />
        </Card>
      </div>
    );
  };

  const renderManagement = () => (
    <div style={{ position: "relative" }}>
      <CreateMeterModal
        visible={isMeteringModalVisible}
        onClose={closeMeteringModal}
        locationId={locationId || ""}
        preselectedSubjectId={meteringPositionId}
        positions={positions}
        assets={assets}
        refetch={refetch}
      />
      <CTMeasurementsModal
        visible={isCTMeasurementsModalVisible}
        onClose={handleCloseCTMeasurements}
        meteringPointPositionId={selectedCTMeteringPointId}
        assetName={selectedCTAssetName || undefined}
      />
      <MeterReadingsModal
        visible={isMeterReadingsModalVisible}
        onClose={handleCloseMeterReadings}
        assetId={selectedAssetId || ""}
        assetName={selectedAssetName || ""}
        locationId={locationId || ""}
      />
      <PositionMeasurementsModal
        visible={isPositionMeasurementsModalVisible}
        onClose={handleClosePositionMeasurements}
        positionId={selectedPositionId || ""}
        positionName={selectedPositionName || ""}
        locationId={locationId || ""}
      />
      <Modal
        title="Rename Position"
        open={isRenameModalVisible}
        onCancel={closeRenameModal}
        onOk={handleRenamePosition}
        okText="Rename"
        cancelText="Cancel"
        confirmLoading={updatingPosition}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="positionNameInput" style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
            Position Name
          </label>
          <Input
            id="positionNameInput"
            value={newPositionName}
            onChange={(e) => setNewPositionName(e.target.value)}
            onPressEnter={handleRenamePosition}
            placeholder="Enter position name"
            autoFocus
          />
        </div>
      </Modal>
      <Spin spinning={updating} tip="Updating asset position..." size="large" style={{ zIndex: 1000 }}>
        {/* Legend */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <span style={{ display: "flex", alignItems: "center", color: "#1976d2", fontSize: 11 }}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                background: "#1976d2",
                borderRadius: 2,
                marginRight: 4,
                border: "1px solid #ccc",
              }}
            />
            Key
          </span>
          <span style={{ display: "flex", alignItems: "center", color: "#6d4caf", fontSize: 11 }}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                background: "#6d4caf",
                borderRadius: 2,
                marginRight: 4,
                border: "1px solid #ccc",
              }}
            />
            Group
          </span>
        </div>
        {/* Filter by type */}
        <div style={{ marginBottom: 8 }}>
          <Checkbox.Group
            options={[
              { label: "Key", value: "key" },
              { label: "Group", value: "group" },
            ]}
            value={positionTypeFilter}
            onChange={(checked) => setPositionTypeFilter(checked as string[])}
            style={{ display: "flex", gap: 8 }}
          />
        </div>
        {/* Search bar and top action buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
          <Input.Search
            placeholder="Search positions or assets"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ maxWidth: 400 }}
            allowClear
          />
          {roleAndPermission.isPC() && (
            <Button type="primary" onClick={openZoneModal} style={{ marginLeft: 8 }}>
              Create Zone
            </Button>
          )}
        </div>
        <AssetModal
          visible={isModalVisible}
          onCancel={closeModal}
          preselectedPositionId={selectedPositionIdForAsset}
          positions={positions}
          refetch={refetch}
          editingAsset={editingAsset}
        />
        <CreateZoneModal
          locationId={locationId || ""}
          visible={isZoneModalVisible}
          onClose={closeZoneModal}
          onCompleted={refetch}
        />
        <DndContext
          onDragEnd={handleDragEnd}
          onDragStart={({ active }) => setActiveId(String(active.id))}
          onDragCancel={() => setActiveId(null)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 16,
              marginTop: 16,
              alignItems: "flex-start",
              position: "relative",
            }}
          >
            {/* Left: Positions */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start" }}>
                {paginatedPositions.map((position: any) => (
                  <div key={position.positionId} style={{ flex: "0 1 280px", minWidth: 260 }}>
                    {renderPositionTree(position)}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalPositions}
                  onChange={setCurrentPage}
                  showSizeChanger
                  onShowSizeChange={(_, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                  pageSizeOptions={[4, 8, 16, 32]}
                />
              </div>
            </div>
            {/* Right: Asset List + Metering Points */}
            <div style={{ flex: "0 0 320px", minWidth: 260 }}>
              <Collapse defaultActiveKey={["assets", "metering"]} style={{ background: "#fff" }}>
                <Collapse.Panel header="Assets" key="assets" style={{ padding: 0 }}>
                  {renderAssetList()}
                </Collapse.Panel>
                <Collapse.Panel header="Meters" key="metering" style={{ padding: 0 }}>
                  <MeteringPointsList
                    positions={positions}
                    assets={data?.sensorflow_assets || []}
                    streamMappings={data?.sensorflow_stream_mappings || []}
                    roleAndPermission={roleAndPermission}
                    refetch={refetch}
                    onLocateMeter={(measuredSubjects: { name: string; type: string; id: string }[]) => {
                      // Use the first measured subject's name for filtering
                      if (measuredSubjects.length > 0) {
                        setSearchTerm(measuredSubjects[0].name);
                        setCurrentPage(1);
                      }
                    }}
                  />
                </Collapse.Panel>
              </Collapse>
            </div>
          </div>
          {/* DragOverlay ensures the dragged asset floats above all content and is never clipped */}
          <DragOverlay>
            {activeAsset ? (
              <DraggableAsset
                asset={activeAsset}
                positionName={getPositionName(assetPositions[activeAsset.id] || activeAsset.parentPositionId)}
                hasMeter={hasActiveAssetMeter(activeAsset.id)}
                isCTNode={isCTNode(activeAsset.id)}
                meterIcon={getMeterIcon(activeAsset.id, true)}
                onViewMeasurements={() =>
                  handleViewCTMeasurements(
                    activeAsset.id,
                    activeAsset.description || activeAsset.type || "Unknown Asset"
                  )
                }
                onViewMeterReadings={() =>
                  handleViewMeterReadings(
                    activeAsset.id,
                    activeAsset.description || activeAsset.type || "Unknown Asset"
                  )
                }
                phases={activeAsset.numOfPhases}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </Spin>
    </div>
  );

  // Helper function to get meter information for an asset
  const getAssetMeterInfo = (assetId: string) => {
    if (!data?.sensorflow_stream_mappings || !data?.positions) return null;

    // Find stream mapping for this asset
    const streamMapping = data.sensorflow_stream_mappings.find(
      (mapping: any) => mapping.subjectId === assetId && !mapping.decommissionedTime
    );

    if (!streamMapping) return null;

    // Find the metering point position
    const meteringPosition = data.positions.find(
      (pos: any) => pos.positionId === streamMapping.meteringPointPositionId
    );

    if (!meteringPosition) return null;

    return {
      meteringPointPositionId: meteringPosition.positionId,
      position: meteringPosition,
    };
  };

  // Helper function to get meter identifier from position
  const getMeterIdentifier = (position: any) => {
    if (position.nodeSlots && position.nodeSlots.length > 0) {
      // Get the first active node slot (not removed)
      const activeSlot = position.nodeSlots.find((slot: any) => !slot.slotRemoveDate);
      const slot = activeSlot || position.nodeSlots[0];

      if (slot) {
        // Check if the slot has mappings to get the actual identifier
        if (slot.slotMappings && slot.slotMappings.length > 0) {
          const activeMapping = slot.slotMappings.find((mapping: any) => !mapping.decomissionedTime);
          const mapping = activeMapping || slot.slotMappings[0];
          if (mapping && mapping.nodeMacId) {
            return mapping.nodeMacId;
          }
        }

        // Fallback to direct slot properties if no mappings found
        // For camera meters, use nodeMacId
        if (slot.subType === "camera" || position.positionName?.toLowerCase().includes("milesight")) {
          return slot.nodeMacId || "";
        }
        // For CT meters, use nodeId
        return slot.nodeId || "";
      }
    }
    return "";
  };

  // Helper function to get GraphQL meter type from position
  const getGraphQLMeterType = (position: any): MeterType => {
    if (position.nodeSlots && position.nodeSlots.length > 0) {
      // Get the first active node slot (not removed)
      const activeSlot = position.nodeSlots.find((slot: any) => !slot.slotRemoveDate);
      const slot = activeSlot || position.nodeSlots[0];

      if (slot) {
        // Camera meters have subType: 'camera'
        if (slot.subType === "camera") {
          return MeterType.Camera;
        }

        // Additional check: Milesight devices are Camera meters, not CT meters
        if (position.positionName && position.positionName.toLowerCase().includes("milesight")) {
          return MeterType.Camera;
        }

        // Check for CT meter (has nodeType: 'energy')
        if (slot.nodeType === "energy") {
          return MeterType.Ct;
        }
      }
    }
    // Default to CT for backward compatibility
    return MeterType.Ct;
  };

  // Handler for unmapping meters from assets
  const handleUnmapAssetMeter = (assetId: string, assetName: string) => {
    const meterInfo = getAssetMeterInfo(assetId);

    if (!meterInfo) {
      Modal.error({
        title: "Error",
        content: "Could not find meter information for this asset.",
      });
      return;
    }

    const identifier = getMeterIdentifier(meterInfo.position);
    const meterType = getGraphQLMeterType(meterInfo.position);

    if (!identifier) {
      Modal.error({
        title: "Error",
        content: "No meter identifier found for this asset.",
      });
      return;
    }

    Modal.confirm({
      title: "Unmap Meter",
      content: `Are you sure you want to unmap the ${meterType.toLowerCase()} meter from '${assetName}'?`,
      okText: "Unmap",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const result = await unmapMeter({
            variables: {
              meteringPointPositionId: meterInfo.meteringPointPositionId,
              meterType: meterType as any,
              identifier,
            },
          });

          if (result.data?.unmapMeter?.success) {
            message.success(`${meterType} meter unmapped successfully from ${assetName}!`);
            refetch();
          } else {
            throw new Error(result.data?.unmapMeter?.message || "Failed to unmap meter");
          }
        } catch (err: any) {
          Modal.error({
            title: "Error",
            content: err.message || "Failed to unmap meter",
          });
        }
      },
      okButtonProps: { loading: unmappingMeter },
    });
  };

  // Handler for unmapping meters from positions/zones
  const handleUnmapPositionMeter = (position: any) => {
    // Find the metering point position that is measuring this position
    // The position passed here is the subject being measured, not the metering point itself
    const streamMapping = data?.sensorflow_stream_mappings?.find(
      (mapping: any) => mapping.subjectId === position.positionId && !mapping.decommissionedTime
    );

    if (!streamMapping) {
      Modal.error({
        title: "Error",
        content: "Could not find meter mapping for this position.",
      });
      return;
    }

    // Find the actual metering point position that contains the meter hardware
    const meteringPointPosition = data?.positions?.find(
      (pos: any) => pos.positionId === streamMapping.meteringPointPositionId
    );

    if (!meteringPointPosition) {
      Modal.error({
        title: "Error",
        content: "Could not find meter information for this position.",
      });
      return;
    }

    const identifier = getMeterIdentifier(meteringPointPosition);
    const meterType = getGraphQLMeterType(meteringPointPosition);

    if (!identifier) {
      Modal.error({
        title: "Error",
        content: "No meter identifier found for this position.",
      });
      return;
    }

    Modal.confirm({
      title: "Unmap Meter",
      content: `Are you sure you want to unmap the ${meterType.toLowerCase()} meter from '${position.positionName}'?`,
      okText: "Unmap",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const result = await unmapMeter({
            variables: {
              meteringPointPositionId: meteringPointPosition.positionId,
              meterType: meterType as any,
              identifier,
            },
          });

          if (result.data?.unmapMeter?.success) {
            message.success(`${meterType} meter unmapped successfully from ${position.positionName}!`);
            refetch();
          } else {
            throw new Error(result.data?.unmapMeter?.message || "Failed to unmap meter");
          }
        } catch (err: any) {
          Modal.error({
            title: "Error",
            content: err.message || "Failed to unmap meter",
          });
        }
      },
      okButtonProps: { loading: unmappingMeter },
    });
  };

  // Handler for opening position measurements modal
  const handleViewPositionMeasurements = (positionId: string, positionName: string) => {
    setSelectedPositionId(positionId);
    setSelectedPositionName(positionName);
    setIsPositionMeasurementsModalVisible(true);
  };

  // Handler for closing position measurements modal
  const handleClosePositionMeasurements = () => {
    setIsPositionMeasurementsModalVisible(false);
    setSelectedPositionId(null);
    setSelectedPositionName(null);
  };

  // Delete handlers
  const handleDeleteAsset = (asset: any) => {
    const deleteCheck = canDeleteAsset(asset.id);

    if (!deleteCheck.canDelete) {
      Modal.error({
        title: "Cannot Delete Asset",
        content: deleteCheck.reason,
      });
      return;
    }

    Modal.confirm({
      title: "Delete Asset",
      content: `Are you sure you want to delete the asset "${
        asset.description || asset.type || "Unknown Asset"
      }"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteAsset({
            variables: {
              id: asset.id,
              deletedAt: new Date().toISOString(),
              deletedBy: user?.sub || null,
            },
          });
        } catch (deleteError: any) {
          Modal.error({
            title: "Error",
            content: deleteError.message || "Failed to delete asset",
          });
        }
      },
      okButtonProps: { loading: deletingAsset },
    });
  };

  const handleDeletePosition = (position: any) => {
    const deleteCheck = canDeletePosition(position.positionId);

    if (!deleteCheck.canDelete) {
      Modal.error({
        title: "Cannot Delete Zone/Position",
        content: deleteCheck.reason,
      });
      return;
    }

    Modal.confirm({
      title: "Delete Zone/Position",
      content: `Are you sure you want to delete the ${getPositionVisualType(position).label.toLowerCase()} "${
        position.positionName
      }"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePosition({
            variables: {
              positionId: position.positionId,
            },
          });
        } catch (deleteError: any) {
          Modal.error({
            title: "Error",
            content: deleteError.message || "Failed to delete zone/position",
          });
        }
      },
      okButtonProps: { loading: deletingPosition },
    });
  };

  return (
    <div className="smart-grd-container">
      <Card>
        <Typography>
          <Title level={3}>SmartGRD</Title>
          <Paragraph>
            Manage and monitor your grid infrastructure with asset mapping, meter management, and analytics.
          </Paragraph>
        </Typography>

        <Tabs defaultActiveKey="dashboard">
          <TabPane tab="Dashboard" key="dashboard">
            {renderDashboard()}
          </TabPane>

          {roleAndPermission.isPC() && (
            <TabPane tab="Management" key="management">
              {renderManagement()}
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default SmartGRD;
